import React, { useEffect, useState } from "react";
import { UtensilsCrossed, ArrowLeft } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { fetchCategories } from "../services/categories.service";

export default function CategoriesPage({ onNavigate }) {
  const { t, lang } = useI18n();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const langMap = { AR: "ar", EN: "en", TR: "tr" };
    const apiLang = langMap[lang] || "ar";

    fetchCategories({ lang: apiLang })
      .then((cats) => { if (!cancelled) setCategories(cats); })
      .catch((err) => console.error("CategoriesPage fetch error:", err));

    return () => { cancelled = true; };
  }, [lang]);

  return (
    <div className="min-h-screen bg-[#f8f8f8] pb-20">
      {/* Hero */}
      <div className="relative h-[60vh] flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
            alt="Dark Food Table"
            className="w-full h-full object-cover opacity-60 animate-slow-scale"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#c32228]/30 via-[#c32228]/10 to-transparent" />
        </div>

        <div className="relative z-10 text-center px-4 pt-10">
          <span className="text-[#c32228] font-bold tracking-[0.4em] text-sm uppercase mb-4 block animate-fade-in-down">
            {t("categoriesPage.tagline")}
          </span>
          <h1 className="text-6xl md:text-8xl font-serif font-bold text-white mb-6 drop-shadow-2xl animate-scale-in">
            {t("categoriesPage.title")}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#c32228] to-[#d6ad60]">
              {t("categoriesPage.titleHighlight")}
            </span>
          </h1>
        </div>
      </div>

      {/* Grid */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categories.map((category, index) => (
            <div
              key={category.id}
              onClick={() => onNavigate?.("products", category.id)}
              className="group relative h-[380px] bg-white rounded-2xl overflow-hidden shadow-lg cursor-pointer transform transition-all duration-500 hover:-translate-y-2 border-2 border-[#c32228]"
              style={{
                animation: `fadeInUp 0.6s ease-out forwards ${index * 0.1}s`,
                opacity: 0,
              }}
            >
              <div className="absolute inset-0">
                <div className="absolute top-4 right-4 z-20 bg-black/60 backdrop-blur-md text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 border border-white/10">
                  <UtensilsCrossed size={12} className="text-[#d6ad60]" />
                  {category.productCount}
                </div>

                <img
                  src={category.imageUrl || "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=600"}
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-80 transition-opacity duration-500" />
              </div>

              <div className="absolute bottom-0 left-0 right-0 z-20">
                <div className="bg-black/40 backdrop-blur-sm p-8 border-t border-white/10">
                  <h3 className="text-3xl font-serif font-bold text-white mb-3 group-hover:text-[#c32228] transition-colors">
                    {category.name}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed mb-4 border-l-2 border-[#c32228] pl-3">
                    {category.description}
                  </p>

                  <span className="inline-flex items-center gap-2 text-[#d6ad60] text-sm font-bold tracking-widest uppercase group-hover:text-white transition-colors">
                    {t("categoriesPage.browseSection")} <ArrowLeft size={16} />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
